/**
 * BookingAuditLog - Displays the activity/change history for a booking
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, History } from 'lucide-react';
import { getBookingAuditLog } from '../services/bookingAuditLog.service';
import { useAuth } from '../contexts/AuthContext';
import { useDateFormatter } from '../hooks/useDateFormatter';
import type { Database } from '../lib/database.types';

type AuditLogRow = Database['public']['Tables']['booking_audit_log']['Row'];

interface BookingAuditLogProps {
  bookingId: string;
}

const STATE_LABELS: Record<string, string> = {
  open: 'Open',
  active: 'Active',
  returned: 'Completed',
  cancelled: 'Cancelled',
  paid: 'Paid',
  open_pay_later: 'Pay Later',
  refunded: 'Refunded',
  partially_refunded: 'Partially Refunded',
};

const TRIGGER_LABELS: Record<string, string> = {
  admin_panel: 'Admin Panel',
  customer: 'Customer',
  api: 'API',
  webhook: 'Webhook',
  system: 'System',
  cron: 'Scheduled Process',
  email_service: 'Email Service',
};

const CHANGE_TYPE_LABELS: Record<string, string> = {
  created: 'Created',
  status_changed: 'Status',
  payment_updated: 'Payment',
  customer_info_updated: 'Customer',
  dates_changed: 'Dates',
  items_added: 'Items',
  items_removed: 'Items',
  discount_applied: 'Discount',
  discount_removed: 'Discount',
  cancelled: 'Cancelled',
  refunded: 'Refund',
  notes_added: 'Notes',
  inventory_reallocated: 'Inventory',
  email_sent: 'Email',
  email_delivered: 'Email',
  email_bounced: 'Email',
  email_failed: 'Email',
  other: 'Updated',
};

function formatStateValue(value: any): string {
  if (value == null) return '-';
  return STATE_LABELS[String(value)] || String(value);
}

function formatTimestamp(iso: string, dateFn: (d: Date | string) => string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  let relative: string;
  if (diffMin < 1) {
    relative = 'just now';
  } else if (diffMin < 60) {
    relative = `${diffMin}m ago`;
  } else if (diffHours < 24) {
    relative = `${diffHours}h ago`;
  } else if (diffDays < 7) {
    relative = `${diffDays}d ago`;
  } else {
    relative = '';
  }

  const absolute = dateFn(date);

  return relative ? `${absolute} (${relative})` : absolute;
}

function describeEntry(entry: AuditLogRow, dateFn: (d: Date | string) => string): string {
  const details = (entry.change_details || {}) as Record<string, any>;

  switch (entry.change_type) {
    case 'created':
      return 'Booking created';

    case 'status_changed':
      return `Booking status changed from "${formatStateValue(details.from)}" to "${formatStateValue(details.to)}"`;

    case 'cancelled':
      return `Booking cancelled${details.from ? ` (was "${formatStateValue(details.from)}")` : ''}`;

    case 'payment_updated': {
      // Off-session damage charges (api/stripe/charge-off-session.js) tag their
      // change_details with action='off_session_charge'. They don't move the
      // booking's payment_state (Option B ledger), so there's no from/to to
      // render — describe the action instead.
      if (details.action === 'off_session_charge') {
        const amt = details.amount;
        const cur = (details.currency || 'EUR').toString().toUpperCase();
        const amountText = amt != null ? ` of ${amt} ${cur}` : '';
        if (entry.success === false) {
          const errCode = details.error_code ? ` (${details.error_code})` : '';
          return `Off-session charge attempt failed${amountText}${errCode}`;
        }
        if (details.requires_action) {
          return `Off-session charge${amountText} requires customer authentication`;
        }
        const reason = details.reason ? `: ${details.reason}` : '';
        return `Off-session charge${amountText} recorded${reason}`;
      }
      if (details.from || details.to) {
        return `Payment status changed from "${formatStateValue(details.from)}" to "${formatStateValue(details.to)}"`;
      }
      return 'Payment updated';
    }

    case 'dates_changed': {
      const fromStart = details.from?.start_date ? dateFn(details.from.start_date) : '?';
      const toStart = details.to?.start_date ? dateFn(details.to.start_date) : '?';
      const fromEnd = details.from?.end_date ? dateFn(details.from.end_date) : '?';
      const toEnd = details.to?.end_date ? dateFn(details.to.end_date) : '?';
      return `Dates changed: ${fromStart}–${fromEnd} → ${toStart}–${toEnd}`;
    }

    case 'customer_info_updated': {
      const fields = Object.keys(details)
        .map(k => k.replace('customer_', '').replace('_', ' '))
        .join(', ');
      return `Customer info updated (${fields})`;
    }

    case 'items_added': {
      const items = details.items;
      return items?.length
        ? `Items added: ${Array.isArray(items) ? items.join(', ') : items}`
        : 'Items added';
    }

    case 'items_removed': {
      const items = details.items;
      return items?.length
        ? `Items removed: ${Array.isArray(items) ? items.join(', ') : items}`
        : 'Items removed';
    }

    case 'notes_added':
      return 'Notes updated';

    case 'discount_applied':
      return `Discount applied${details.code ? `: ${details.code}` : ''}`;

    case 'discount_removed':
      return 'Discount removed';

    case 'refunded':
      return `Refund processed${details.amount ? ` (${details.amount})` : ''}`;

    case 'inventory_reallocated': {
      const product = details.product_name || '';
      const from = details.from_item;
      const to = details.to_item;
      if (details.swap && details.swapped_with_booking_order_id) {
        return `${product} swapped: ${from || '?'} → ${to || '?'} (swapped with booking ${details.swapped_with_booking_order_id})`;
      }
      if (from && to) {
        const reasonLabel = details.reason === 'issue' ? ' (due to issue)'
          : details.reason === 'date_change_conflict' ? ' (date conflict)'
          : details.reason === 'blocking_period' ? ' (blocking period)'
          : '';
        return `${product} reallocated: ${from} → ${to}${reasonLabel}`;
      }
      return product ? `${product} reallocated` : 'Inventory reallocated';
    }

    case 'email_sent':
      return `Email sent${details.template ? `: ${details.template}` : ''}`;

    case 'email_delivered':
      return 'Email delivered';

    case 'email_bounced':
      return 'Email bounced';

    case 'email_failed':
      return 'Email delivery failed';

    case 'other':
      return details.description || 'Booking updated';

    default:
      return entry.change_type.replace(/_/g, ' ');
  }
}

function getChangeTypeBadge(changeType: string) {
  switch (changeType) {
    case 'created':
      return 'badge-success';
    case 'status_changed':
    case 'cancelled':
      return 'badge-info';
    case 'payment_updated':
    case 'refunded':
      return 'badge-warning';
    case 'items_added':
    case 'items_removed':
    case 'inventory_reallocated':
      return 'badge-info';
    case 'email_bounced':
    case 'email_failed':
      return 'badge-danger';
    default:
      return 'badge-info';
  }
}

function renderDetails(entry: AuditLogRow) {
  const details = (entry.change_details || {}) as Record<string, any>;

  // For status/payment changes, show from → to
  if ((entry.change_type === 'status_changed' || entry.change_type === 'payment_updated' || entry.change_type === 'cancelled') && details.from && details.to) {
    return (
      <p className="text-xs text-tertiary mt-xs">
        <span className="line-through">{formatStateValue(details.from)}</span>
        {' → '}
        {formatStateValue(details.to)}
      </p>
    );
  }

  // For customer info updates, show which fields changed
  if (entry.change_type === 'customer_info_updated') {
    const changedFields = Object.entries(details);
    if (changedFields.length > 0) {
      return (
        <div className="text-xs text-tertiary mt-xs space-y-0.5">
          {changedFields.map(([field, change]: [string, any]) => (
            <p key={field}>
              {field.replace('customer_', '').replace('_', ' ')}:{' '}
              <span className="line-through">{String(change?.from ?? '-')}</span>
              {' → '}
              {String(change?.to ?? '-')}
            </p>
          ))}
        </div>
      );
    }
  }

  // For inventory reallocations, show from → to
  if (entry.change_type === 'inventory_reallocated' && details.from_item && details.to_item) {
    return (
      <p className="text-xs text-tertiary mt-xs">
        <span className="line-through">{details.from_item}</span>
        {' → '}
        {details.to_item}
      </p>
    );
  }

  // For financial changes
  if (entry.change_type === 'other' && details.changes) {
    const changedFields = Object.entries(details.changes);
    if (changedFields.length > 0) {
      return (
        <div className="text-xs text-tertiary mt-xs space-y-0.5">
          {changedFields.map(([field, change]: [string, any]) => (
            <p key={field}>
              {field.replace(/_/g, ' ')}:{' '}
              <span className="line-through">{String(change?.from ?? '-')}</span>
              {' → '}
              {String(change?.to ?? '-')}
            </p>
          ))}
        </div>
      );
    }
  }

  return null;
}

export default function BookingAuditLog({ bookingId }: BookingAuditLogProps) {
  const { t } = useTranslation();
  const { formatDateShort, formatDateTimeCompact } = useDateFormatter();
  const { accessControl } = useAuth();
  const [entries, setEntries] = useState<AuditLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAuditLog();
  }, [bookingId]);

  async function loadAuditLog() {
    setLoading(true);
    setError(null);
    const { data, error: loadError } = await getBookingAuditLog(bookingId, accessControl);
    if (loadError) {
      setError(loadError);
    } else {
      setEntries(data || []);
    }
    setLoading(false);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-xl">
        <Loader2 className="w-6 h-6 text-blue animate-spin" />
        <span className="ml-md text-secondary">{t('bookingAuditLog.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-lg text-center text-secondary">
        {t('bookingAuditLog.failedToLoad')}: {error}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="p-lg text-center text-secondary">
        <History className="w-8 h-8 mx-auto mb-md text-tertiary" />
        <p>{t('bookingAuditLog.noActivity')}</p>
      </div>
    );
  }

  // Group entries by date
  const grouped = new Map<string, AuditLogRow[]>();
  for (const entry of entries) {
    if (!entry.created_at) continue;
    const dateKey = formatDateShort(entry.created_at);
    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }
    grouped.get(dateKey)!.push(entry);
  }

  return (
    <div className="space-y-lg">
      {Array.from(grouped.entries()).map(([dateLabel, dayEntries]) => (
        <div key={dateLabel}>
          <h4 className="text-sm font-medium text-secondary mb-md">{dateLabel}</h4>
          <div className="space-y-sm">
            {dayEntries.map((entry) => (
              <div
                key={entry.id}
                className="flex items-start gap-md p-md rounded-lg bg-secondary/5 border border-primary/10"
              >
                {/* Timeline dot */}
                <div className="mt-1.5 w-2 h-2 rounded-full bg-blue flex-shrink-0" />

                <div className="flex-1 min-w-0">
                  {/* Description */}
                  <p className="text-sm text-primary">
                    {describeEntry(entry, formatDateTimeCompact)}
                  </p>

                  {/* Value change details */}
                  {renderDetails(entry)}

                  {/* Meta line: who + when + trigger */}
                  <div className="flex flex-wrap items-center gap-xs mt-xs text-xs text-tertiary">
                    {entry.user_email && (
                      <span>by {entry.user_email}</span>
                    )}
                    <span>via {TRIGGER_LABELS[entry.triggered_by] || entry.triggered_by}</span>
                    <span className="text-tertiary/50">&middot;</span>
                    {entry.created_at && (
                      <span>{formatTimestamp(entry.created_at, formatDateTimeCompact)}</span>
                    )}
                  </div>
                </div>

                {/* Change type badge */}
                <span className={`badge ${getChangeTypeBadge(entry.change_type)} text-xs flex-shrink-0`}>
                  {CHANGE_TYPE_LABELS[entry.change_type] || entry.change_type}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
